import { AfterViewInit, Component, DestroyRef, ElementRef, OnDestroy, OnInit, TemplateRef, ViewChild, ViewContainerRef, computed, effect, inject, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormControl, FormRecord } from '@angular/forms';
import { MatBottomSheetRef, MAT_BOTTOM_SHEET_DATA } from '@angular/material/bottom-sheet';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { TranslateService } from '@ngx-translate/core';
import { DataService } from '../../services/data.service';
import { CartLine, CartService, ITEM_NOTE_MAX_LENGTH } from '../../services/cart.service';
import { SelectedModifier } from '../../models/cart.model';
import { Item, Modifier, ModifierGroup } from '../../models/menu.model';
import { ItemStatus, VenueType } from '../enums';
import { MenuService } from '../../menu/menu.service';
import { findItemById } from '../../menu/menu.utils';
import { ToastService } from '../../services/toast.service';
import { ThemeService } from '../../services/theme.service';

export interface InputData {
  item: Item;
  subSectionId?: string;
  isOptionalSubSection?: boolean;
  // Present when editing an existing cart line rather than adding a new one - the sheet
  // is pre-filled from it, and confirm() replaces it in place instead of adding/merging.
  cartLine?: CartLine;
}

@Component({
  selector: 'app-modifier',
  templateUrl: './modifier.component.html',
  styleUrls: ['./modifier.component.scss'],
  standalone: false
})
export class ModifierComponent implements OnInit, AfterViewInit, OnDestroy {

  public sheetRef = inject<MatBottomSheetRef<ModifierComponent>>(MatBottomSheetRef);
  public data = inject<InputData>(MAT_BOTTOM_SHEET_DATA);

  private toastService = inject(ToastService);
  private translateService = inject(TranslateService);
  private dataService = inject(DataService);
  private cartService = inject(CartService);
  private menuService = inject(MenuService);
  private destroyRef = inject(DestroyRef);
  private overlay = inject(Overlay);
  private viewContainerRef = inject(ViewContainerRef);
  private elementRef = inject(ElementRef<HTMLElement>);

  public readonly placeholderImage = inject(ThemeService).placeholderImage;

  // Rendered via CDK Overlay (attached to the global overlay container on <body>)
  // rather than in-place, since the bottom sheet's own container applies a transform
  // that would otherwise clip a `position: fixed` full-screen preview to its bounds.
  @ViewChild('imagePreviewTpl') private imagePreviewTpl!: TemplateRef<unknown>;
  private imagePreviewOverlayRef: OverlayRef | null = null;

  private fullHeightObserver: ResizeObserver | null = null;

  // Live-updated as menu.updated events land, so price/availability changes reflect
  // while the sheet is open. undefined means the item is no longer on the menu at all.
  private readonly currentItem = computed(() => findItemById(this.menuService.menu()?.sections ?? [], this.data.item.id));

  // One control per modifier group: FormControl<string|null> for single/single-compulsory,
  // nested FormRecord of boolean/number controls (keyed by modifier id) for multiple/min-max.
  public form = new FormRecord<AbstractControl>({});
  public note = new FormControl('', { nonNullable: true });
  public readonly noteMaxLength = ITEM_NOTE_MAX_LENGTH;
  public total = signal(0);
  public submitted = signal(false);
  public quantity = signal(1);
  public readonly isViewOnly = this.dataService.restaurant().type === VenueType.ReadOnly
    || this.data.item.status === ItemStatus.ReadOnly;
  // Live price for the view-only header, which has no add-to-cart button to carry it.
  public readonly displayedItem = computed(() => this.currentItem() ?? this.data.item);
  // The template renders the opening snapshot (data.item), so this hides any modifier
  // that has since dropped off the live menu.
  private readonly liveModifierIds = computed(() => new Set(
    this.displayedItem().modifier_groups.flatMap(modifierGroup => modifierGroup.modifiers.map(modifier => modifier.id))
  ));
  public readonly isEditMode = !!this.data.cartLine;

  // Per-group required-selection validity, keyed by group id. Kept in a signal
  // here rather than mutated onto the (shared, cached) ModifierGroup data.
  private readonly groupValidity = signal<Record<string, boolean>>({});

  constructor() {
    effect(() => {
      const item = this.currentItem();
      const stillShowable = this.isViewOnly ? item && item.status !== ItemStatus.Unavailable : item?.status === ItemStatus.Enabled;
      if (stillShowable) return;

      this.sheetRef.dismiss();
      this.toastService.info(this.translateService.instant('COMMON.ITEM_UNAVAILABLE'));
    });

    // Keeps the displayed total in sync with live price changes to the item/modifiers.
    effect(() => {
      this.currentItem();
      this.calculateTotal();
    });

    // A modifier absent from a live menu update is unavailable (same rule the cart applies
    // in TableSocketService) - drop any selection of it so it can't be added to the cart.
    effect(() => {
      const liveIds = this.liveModifierIds();
      untracked(() => this.clearUnavailableSelections(liveIds));
    });
  }

  public ngOnInit(): void {
    for (const modifierGroup of this.data.item.modifier_groups) {
      this.initModifierGroup(modifierGroup);
    }
    if (this.data.cartLine) {
      this.applyCartLineSelections(this.data.cartLine);
    }
    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.calculateTotal());
    this.calculateTotal();
  }

  public ngAfterViewInit(): void {
    const container = this.elementRef.nativeElement.closest('.mat-bottom-sheet-container') as HTMLElement | null;
    if (!container) return;

    this.fullHeightObserver = new ResizeObserver(() =>
      container.classList.toggle('full-height', container.getBoundingClientRect().top <= 1)
    );
    this.fullHeightObserver.observe(container);
  }

  public getControl(groupId: string, modifierId?: string): FormControl {
    const groupControl = this.form.get(groupId);
    return (modifierId === undefined ? groupControl : groupControl?.get(modifierId)) as FormControl;
  }

  public isGroupValid(groupId: string): boolean {
    return this.groupValidity()[groupId] ?? true;
  }

  public availableModifiers(modifierGroup: ModifierGroup): Modifier[] {
    const liveIds = this.liveModifierIds();
    return modifierGroup.modifiers.filter(modifier => liveIds.has(modifier.id));
  }

  /**
   * This method is used for the modifier type 'single', as you can
   * choose 0 or 1 modifier. This gives you the possibility to deselect
   * a modifier you already selected.
   */
  public deselect(modifierGroupId: string, modifierId: string): void {
    const control = this.getControl(modifierGroupId);

    if (control.value === modifierId) {
      control.setValue(null);
    }
  }

  public check(modifierGroup: ModifierGroup): void {
    const numberOfChoices = this.getNumberOfChoices(modifierGroup);
    const min: number = modifierGroup.type_min || 0;
    const max: number = modifierGroup.type_max || modifierGroup.modifiers.length;

    this.setGroupValid(modifierGroup.id, numberOfChoices >= min);
    this.updateMinMaxAvailability(modifierGroup, numberOfChoices, max);
  }

  public incrementModifier(modifierGroup: ModifierGroup, modifierId: string): void {
    const max: number = modifierGroup.type_max || 1;
    const numberOfChoices = this.getModifierQuantityTotal(modifierGroup);
    if (numberOfChoices >= max) {
      return;
    }

    const control = this.getControl(modifierGroup.id, modifierId);
    control.setValue(control.value + 1);
    this.updateMinMaxValidity(modifierGroup, numberOfChoices + 1);
  }

  public decrementModifier(modifierGroup: ModifierGroup, modifierId: string): void {
    const numberOfChoices = this.getModifierQuantityTotal(modifierGroup);
    if (numberOfChoices <= 0) {
      return;
    }

    const control = this.getControl(modifierGroup.id, modifierId);
    control.setValue(control.value - 1);
    this.updateMinMaxValidity(modifierGroup, numberOfChoices - 1);
  }

  public updateQuantity(n: number): void {
    if (this.quantity() === 1 && n === -1) {
      return;
    }
    this.quantity.update(q => q + n);
    this.calculateTotal();
  }

  public onChange(modifierGroup: ModifierGroup): void {
    // The dropdown's blank "-" option has id '' - picking it back counts as no selection.
    this.setGroupValid(modifierGroup.id, !!this.getControl(modifierGroup.id).value);
  }

  public openImagePreview(): void {
    if (this.imagePreviewOverlayRef) {
      return;
    }

    this.imagePreviewOverlayRef = this.overlay.create({
      hasBackdrop: true,
      backdropClass: 'image-preview-backdrop',
      panelClass: 'image-preview-panel',
      positionStrategy: this.overlay.position().global().centerHorizontally().centerVertically(),
      scrollStrategy: this.overlay.scrollStrategies.block(),
    });

    this.imagePreviewOverlayRef.backdropClick().subscribe(() => this.closeImagePreview());
    this.imagePreviewOverlayRef.attach(new TemplatePortal(this.imagePreviewTpl, this.viewContainerRef));
  }

  public closeImagePreview(): void {
    this.imagePreviewOverlayRef?.dispose();
    this.imagePreviewOverlayRef = null;
  }

  public ngOnDestroy(): void {
    this.imagePreviewOverlayRef?.dispose();
    this.fullHeightObserver?.disconnect();
  }

  public confirm(confirm: boolean): void {
    if (!confirm) {
      this.sheetRef.dismiss();
      return;
    }

    this.submitted.set(true);

    if (!this.checkQuanitities()) {
      this.toastService.error(this.translateService.instant('COMMON.MISSING_SELECTIONS'));
      return;
    }

    if (this.data.cartLine) {
      this.cartService.updateLine(this.data.cartLine.id, this.getSelectedModifiers(), this.quantity(), this.note.value.trim() || undefined);
    } else {
      this.cartService.add(this.itemSnapshot(), this.getSelectedModifiers(), this.data.subSectionId, this.data.isOptionalSubSection, this.quantity(), this.note.value.trim() || undefined);
    }
    this.sheetRef.dismiss();
  }

  private initModifierGroup(modifierGroup: ModifierGroup): void {
    this.setGroupValid(modifierGroup.id, true);

    switch (modifierGroup.type) {
      case 'single':
      case 'single-compulsory':
        this.initSingleModifierGroup(modifierGroup);
        break;
      case 'multiple':
        this.initMultipleModifierGroup(modifierGroup);
        break;
      case 'min-max':
        this.initMinMaxModifierGroup(modifierGroup);
        break;
      default:
        console.error('Unknown modifier group type', modifierGroup);
    }
  }

  private initSingleModifierGroup(modifierGroup: ModifierGroup): void {
    // Dropdowns start on the template's blank "-" option (value ''), rendered there rather
    // than pushed into the (shared, cached) modifiers array.
    const initialValue = modifierGroup.display_type === 'dropdown' ? '' : null;
    this.form.addControl(modifierGroup.id, new FormControl<string | null>(initialValue));

    if (modifierGroup.type === 'single-compulsory') {
      this.setGroupValid(modifierGroup.id, false);
    }
  }

  private initMultipleModifierGroup(modifierGroup: ModifierGroup): void {
    const group = new FormRecord<AbstractControl>({});

    if (modifierGroup.type_max) {
      if (modifierGroup.type_min && modifierGroup.type_min >= 1) {
        this.setGroupValid(modifierGroup.id, false);
      }
      for (const modifier of modifierGroup.modifiers) {
        group.addControl(modifier.id, new FormControl<number>(0, { nonNullable: true }));
      }
    } else {
      for (const modifier of modifierGroup.modifiers) {
        group.addControl(modifier.id, new FormControl<boolean>(false, { nonNullable: true }));
      }
    }

    this.form.addControl(modifierGroup.id, group);
  }

  private initMinMaxModifierGroup(modifierGroup: ModifierGroup): void {
    const group = new FormRecord<AbstractControl>({});
    for (const modifier of modifierGroup.modifiers) {
      group.addControl(modifier.id, new FormControl<boolean>(false, { nonNullable: true }));
    }
    this.form.addControl(modifierGroup.id, group);

    if (modifierGroup.type_min && modifierGroup.type_min >= 1) {
      this.setGroupValid(modifierGroup.id, false);
    }
  }

  // Pre-fills the form/quantity/note from an existing cart line's selections, once the
  // (blank) controls have already been built by initModifierGroup(). Re-runs each group
  // type's own validity/availability update so an already-complete selection opens valid.
  private applyCartLineSelections(cartLine: CartLine): void {
    this.quantity.set(cartLine.quantity);
    this.note.setValue(cartLine.comment ?? '');

    const selectedById = new Map(cartLine.modifiers.map(modifier => [modifier.id, modifier.quantity]));

    for (const modifierGroup of this.data.item.modifier_groups) {
      switch (modifierGroup.type) {
        case 'single':
        case 'single-compulsory': {
          const selected = modifierGroup.modifiers.find(modifier => selectedById.has(modifier.id));
          if (selected) {
            this.getControl(modifierGroup.id).setValue(selected.id);
            this.onChange(modifierGroup);
          }
          break;
        }
        case 'multiple':
          for (const modifier of modifierGroup.modifiers) {
            const selectedQuantity = selectedById.get(modifier.id);
            if (!selectedQuantity) continue;
            this.getControl(modifierGroup.id, modifier.id).setValue(modifierGroup.type_max ? selectedQuantity : true);
          }
          if (modifierGroup.type_max) {
            const total = this.getModifierQuantityTotal(modifierGroup);
            this.updateMinMaxValidity(modifierGroup, total);
            this.updateMinMaxAvailability(modifierGroup, total, modifierGroup.type_max);
          }
          break;
        case 'min-max':
          for (const modifier of modifierGroup.modifiers) {
            if (selectedById.has(modifier.id)) {
              this.getControl(modifierGroup.id, modifier.id).setValue(true);
            }
          }
          this.check(modifierGroup);
          break;
      }
    }
  }

  // Resets each selection of a no-longer-available modifier to its empty value, then
  // re-runs that group type's validity/availability update - so a compulsory group
  // flips back to "Required" and a min-max group re-enables its freed-up checkboxes.
  private clearUnavailableSelections(liveIds: Set<string>): void {
    for (const modifierGroup of this.data.item.modifier_groups) {
      // The effect's first run can land before ngOnInit() has built the controls.
      if (!this.form.contains(modifierGroup.id)) continue;

      switch (modifierGroup.type) {
        case 'single':
        case 'single-compulsory': {
          const control = this.getControl(modifierGroup.id);
          if (control.value && !liveIds.has(control.value)) {
            control.setValue(modifierGroup.display_type === 'dropdown' ? '' : null);
            if (modifierGroup.type === 'single-compulsory') {
              this.onChange(modifierGroup);
            }
          }
          break;
        }
        case 'multiple':
        case 'min-max': {
          const removedControls = modifierGroup.modifiers
            .filter(modifier => !liveIds.has(modifier.id))
            .map(modifier => this.getControl(modifierGroup.id, modifier.id))
            .filter(control => control.value);
          if (!removedControls.length) break;

          for (const control of removedControls) {
            control.setValue(typeof control.value === 'number' ? 0 : false);
          }
          if (modifierGroup.type === 'min-max') {
            this.check(modifierGroup);
          } else if (modifierGroup.type_max) {
            const total = this.getModifierQuantityTotal(modifierGroup);
            this.updateMinMaxValidity(modifierGroup, total);
            this.updateMinMaxAvailability(modifierGroup, total, modifierGroup.type_max);
          }
          break;
        }
      }
    }
  }

  private updateMinMaxAvailability(modifierGroup: ModifierGroup, numberOfChoices: number, max: number): void {
    const group = this.form.get(modifierGroup.id) as FormRecord<FormControl<boolean>>;
    const atMax = numberOfChoices >= max;

    for (const modifier of modifierGroup.modifiers) {
      const control = group.get(modifier.id) as FormControl<boolean>;
      if (atMax && !control.value) {
        control.disable({ emitEvent: false });
      } else if (control.disabled) {
        control.enable({ emitEvent: false });
      }
    }
  }

  private updateMinMaxValidity(modifierGroup: ModifierGroup, numberOfChoices: number): void {
    const min = modifierGroup.type_min || 0;
    this.setGroupValid(modifierGroup.id, numberOfChoices >= min);
  }

  private setGroupValid(groupId: string, valid: boolean): void {
    this.groupValidity.update(validity => ({ ...validity, [groupId]: valid }));
  }

  private checkQuanitities(): boolean {
    return this.data.item.modifier_groups.every((modifierGroup) => this.isGroupValid(modifierGroup.id));
  }

  private getNumberOfChoices(modifierGroup: ModifierGroup): number {
    const values = this.form.get(modifierGroup.id)?.value as Record<string, boolean>;
    return Object.values(values).filter(Boolean).length;
  }

  private getModifierQuantityTotal(modifierGroup: ModifierGroup): number {
    const values = this.form.get(modifierGroup.id)?.value as Record<string, number>;
    return Object.values(values).reduce((sum, value) => sum + value, 0);
  }

  private calculateTotal(): void {
    const modifiersTotal = Object.values(this.form.getRawValue())
      .flatMap((value) => this.toSelectedModifiers(value))
      // A selection can briefly outlive its modifier on a live update, until
      // clearUnavailableSelections() drops it - skip rather than throw.
      .reduce((sum, mod) => sum + (this.findModifierById(mod.id)?.price ?? 0) * mod.quantity, 0);

    this.total.set((this.itemSnapshot().price + modifiersTotal) * this.quantity());
  }

  // Falls back to the item as it was when the sheet opened once it's no longer on the
  // live menu — by then the effect above is already dismissing the sheet.
  private itemSnapshot(): Item {
    return this.currentItem() ?? this.data.item;
  }

  private findModifierById(id: string): Modifier | undefined {
    return this.flattenModifiers().find((modifier) => modifier.id === id);
  }

  private flattenModifiers(): Modifier[] {
    return this.itemSnapshot().modifier_groups
      .reduce((accumulator, modifierGroup) => accumulator.concat(modifierGroup.modifiers), [] as Modifier[]);
  }

  private getSelectedModifiers(): SelectedModifier[] {
    return Object.values(this.form.getRawValue()).flatMap((value) => this.toSelectedModifiers(value));
  }

  private toSelectedModifiers(value: unknown): SelectedModifier[] {
    if (typeof value === 'string') {
      return value ? [{ id: value, quantity: 1 }] : [];
    }

    if (value !== null && typeof value === 'object') {
      return Object.entries(value as Record<string, boolean | number>)
        .filter(([, modValue]) => (typeof modValue === 'boolean' && modValue) || (typeof modValue === 'number' && modValue > 0))
        .map(([id, modValue]) => ({ id, quantity: typeof modValue === 'number' ? modValue : 1 }));
    }

    return [];
  }

}
